import React, { ReactNode, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../styles/GlobalStyles';
import { APP_VERSION, ENVIRONMENT } from '../version';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: var(--background-color);
`;

const Sidebar = styled.aside<{ $isOpen: boolean }>`
  width: 250px;
  background: var(--card-background);
  border-right: 1px solid var(--border-color);
  display: flex;
  flex-direction: column;
  position: fixed;
  height: 100vh;
  left: 0;
  top: 0;
  z-index: 1000;
  transition: transform 0.3s ease;

  @media (max-width: 767px) {
    transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(-100%)'};
  }
`;

const SidebarHeader = styled.div`
  padding: var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
`;

const Logo = styled.h1`
  color: var(--primary-color);
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.4;
`;

const LogoSubtitle = styled.div`
  color: var(--text-secondary);
  font-size: 0.75rem;
  margin-top: var(--spacing-xs);
  font-weight: 500;
`;

const Navigation = styled.nav`
  flex: 1;
  padding: var(--spacing-md) 0;
  overflow-y: auto;
`;

const NavLink = styled(Link)<{ $active: boolean }>`
  display: flex;
  align-items: center;
  padding: var(--spacing-md) var(--spacing-lg);
  color: ${props => props.$active ? 'var(--primary-color)' : 'var(--text-secondary)'};
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
  background: ${props => props.$active ? 'var(--primary-color)10' : 'transparent'};
  border-left: 3px solid ${props => props.$active ? 'var(--primary-color)' : 'transparent'};
  min-height: 44px; /* Touch target minimum */

  &:hover {
    color: var(--primary-color);
    background: var(--primary-color)10;
  }

  svg {
    margin-right: var(--spacing-md);
    width: 20px;
    height: 20px;
  }
`;

const SidebarFooter = styled.div`
  padding: var(--spacing-lg);
  border-top: 1px solid var(--border-color);
`;

const UserInfo = styled.div`
  margin-bottom: var(--spacing-md);
`;

const UserName = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: var(--spacing-xs);
  font-size: 0.875rem;
`;

const UserRole = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: 250px;
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  @media (max-width: 767px) {
    margin-left: 0;
  }
`;

const TopBar = styled.header`
  background: var(--card-background);
  border-bottom: 1px solid var(--border-color);
  padding: var(--spacing-md) var(--spacing-lg);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
`;

const HamburgerButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--spacing-sm);
  color: var(--text-primary);
  min-width: 44px;
  min-height: 44px;
  align-items: center;
  justify-content: center;

  @media (max-width: 767px) {
    display: flex;
  }

  &:hover {
    color: var(--primary-color);
  }
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;

  @media (max-width: 767px) {
    font-size: 1.25rem;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: var(--spacing-xl);

  @media (max-width: 767px) {
    padding: var(--spacing-md);
  }
`;

const Footer = styled.footer`
  background: var(--card-background);
  border-top: 1px solid var(--border-color);
  padding: var(--spacing-md) var(--spacing-lg);
  text-align: center;
`;

const FooterContent = styled.div`
  color: var(--text-secondary);
  font-size: 0.875rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-md);
  flex-wrap: wrap;
`;

const VersionBadge = styled.span`
  background: var(--primary-color)15;
  color: var(--primary-color);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  font-family: 'Courier New', monospace;
  font-size: 0.75rem;
  font-weight: 600;
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  display: none;

  @media (max-width: 767px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }
`;

interface AdminLayoutProps {
  children: ReactNode;
  pageTitle?: string;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children, pageTitle }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  // Close sidebar on route change (mobile)
  useEffect(() => {
    closeSidebar();
  }, [location.pathname]);

  // Log version to console on mount
  useEffect(() => {
    console.log(`%c🏷️ Admin Portal Version: ${APP_VERSION}`, 'color: #1976d2; font-weight: bold; font-size: 14px;');
    console.log(`%c🌍 Environment: ${ENVIRONMENT}`, 'color: #1976d2; font-weight: bold; font-size: 14px;');
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/administration-club');
  };

  return (
    <LayoutContainer>
      <Overlay $isOpen={isSidebarOpen} onClick={closeSidebar} />
      
      <Sidebar $isOpen={isSidebarOpen}>
        <SidebarHeader>
          <Logo>Club Administration</Logo>
          <LogoSubtitle>Player Development System</LogoSubtitle>
        </SidebarHeader>

        <Navigation>
          <NavLink 
            to="/administration-club/dashboard" 
            $active={isActive('/administration-club/dashboard')}
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Dashboard
          </NavLink>

          <NavLink 
            to="/administration-club/teams" 
            $active={isActive('/administration-club/teams')}
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Teams
          </NavLink>

          <NavLink 
            to="/administration-club/contacts" 
            $active={isActive('/administration-club/contacts')}
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            Contacts
          </NavLink>

          <NavLink 
            to="/administration-club/settings" 
            $active={isActive('/administration-club/settings')}
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Settings
          </NavLink>
        </Navigation>

        <SidebarFooter>
          <UserInfo>
            <UserName>
              {user?.profile.firstName} {user?.profile.lastName}
            </UserName>
            <UserRole>{user?.role}</UserRole>
          </UserInfo>
          <Button $variant="secondary" onClick={handleLogout} style={{ width: '100%' }}>
            Sign Out
          </Button>
        </SidebarFooter>
      </Sidebar>

      <MainContent>
        <TopBar>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
            <HamburgerButton onClick={toggleSidebar} aria-label="Toggle menu">
              <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </HamburgerButton>
            {pageTitle && <PageTitle>{pageTitle}</PageTitle>}
          </div>
        </TopBar>

        <ContentArea>{children}</ContentArea>

        <Footer>
          <FooterContent>
            <span>© {new Date().getFullYear()} Player Development System</span>
            <span>•</span>
            <VersionBadge>v{APP_VERSION}</VersionBadge>
            {ENVIRONMENT && ENVIRONMENT !== 'production' && (
              <>
                <span>•</span>
                <span style={{ textTransform: 'uppercase', fontWeight: 600 }}>
                  {ENVIRONMENT}
                </span>
              </>
            )}
          </FooterContent>
        </Footer>
      </MainContent>
    </LayoutContainer>
  );
};
